import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { feedbackApi, CreateFeedbackPayload } from '../../api/feedbackApi';
import { useAuthStore } from '../../stores/auth';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onFeedbackSubmitted: () => void;
}

export const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
  onFeedbackSubmitted,
}) => {
  const [feedbackText, setFeedbackText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const { user } = useAuthStore();

  const handleSubmit = async () => {
    if (!feedbackText.trim()) {
      setError('Feedback cannot be empty.');
      return;
    }
    if (!user?.email) {
      setError('User not authenticated.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const payload: CreateFeedbackPayload = { feedbackText };
      await feedbackApi.submitFeedback(payload);
      setFeedbackText('');
      onFeedbackSubmitted();
      onClose();
    } catch (err) {
      setError('Failed to submit feedback. Please try again.');
      console.error('Feedback submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-white rounded-2xl shadow-xl p-6 sm:p-8">
        <DialogHeader className="text-center">
          <DialogTitle className="text-lg sm:text-xl font-bold text-blue-700">
            Provide Feedback
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-600 mt-1">
            We value your input! Please share any thoughts or suggestions.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 grid gap-4">
          <Textarea
            id="feedback"
            placeholder="Your feedback here..."
            value={feedbackText}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setFeedbackText(e.target.value)
            }
            rows={5}
            className="w-full border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 rounded-lg p-3 text-sm resize-none placeholder-gray-400"
          />
          {error && <p className="text-red-500 text-sm">{error}</p>}
        </div>

        <DialogFooter className="mt-4 flex justify-end gap-3">
          <Button
            onClick={onClose}
            variant="outline"
            className="border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
